import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/translations';
import './VideoGrid.css';

const IconFullscreen = () => (
  <svg viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>
);
const IconExitFullscreen = () => (
  <svg viewBox="0 0 24 24"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>
);

// ─── Autoplay-with-sound unlock ──────────────────────────────────────────────
// Browsers can silently block an unmuted <video>'s audio while still rendering
// its video frames (the element never errors, it just never produces sound).
// This mostly hits participants with no prior "media engagement" on this site —
// e.g. a guest opening the meeting link for the first time — so the remote
// video appears to work while the remote audio never does. Queue the element
// and retry play() on the next user gesture anywhere on the page.
const pendingPlays = new Set();
let gestureListenerAttached = false;

function retryPendingPlays() {
  pendingPlays.forEach(el => {
    el.play().then(() => pendingPlays.delete(el)).catch(() => {});
  });
}

function ensureGestureListener() {
  if (gestureListenerAttached) return;
  gestureListenerAttached = true;
  ['click', 'touchstart', 'keydown'].forEach(evt =>
    document.addEventListener(evt, retryPendingPlays, { passive: true })
  );
}

/** Deterministic color per participant (stable across re-renders, varied between people). */
function colorForSeed(seed) {
  const str = String(seed || '');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 62%, 46%)`;
}

/**
 * VideoGrid — vertical roster of participant tiles (200×200) on the right side.
 * When someone is screen-sharing, their feed also takes over a large "main
 * stage" area (the space the sidebar doesn't use) so it's actually readable.
 *
 * Props:
 *   localStream        {MediaStream|null}   — your own camera/mic stream
 *   localName          {string}             — your display name
 *   localMicOn         {boolean}
 *   localCameraOn      {boolean}
 *   localScreenSharing {boolean}
 *   peers              {Array<{
 *                        connectionId: string,
 *                        stream: MediaStream|null,
 *                        displayName: string,
 *                        micOn: boolean,
 *                        cameraOn: boolean,
 *                        screenSharing: boolean,
 *                      }>}
 */
export default function VideoGrid({
  localStream,
  localName,
  localPhotoData,
  localMicOn,
  localCameraOn,
  localScreenSharing,
  peers,
}) {
  const sharingPeer = peers.find(p => p.screenSharing);
  const spotlight = localScreenSharing
    ? { stream: localStream, displayName: localName, photoData: localPhotoData, micOn: localMicOn, isLocal: true }
    : sharingPeer
    ? { stream: sharingPeer.stream, displayName: sharingPeer.displayName, photoData: sharingPeer.photoData, micOn: sharingPeer.micOn, isLocal: false }
    : null;

  return (
    <div className={`vgrid-area${spotlight ? ' vgrid-area--spotlight' : ''}`}>
      {spotlight && (
        <div className="vgrid__main">
          <Tile
            stream={spotlight.stream}
            displayName={spotlight.displayName}
            photoData={spotlight.photoData}
            micOn={spotlight.micOn}
            cameraOn // screen-share content is always the thing to show
            isLocal={spotlight.isLocal}
            avatarColor={colorForSeed(spotlight.displayName || 'main')}
            main
          />
        </div>
      )}

      <div className="vgrid">
        {/* Local tile */}
        <Tile
          stream={localStream}
          displayName={localName}
          photoData={localPhotoData}
          micOn={localMicOn}
          cameraOn={localCameraOn}
          avatarColor={colorForSeed(localName || 'local')}
          isLocal
        />

        {/* Remote peers */}
        {peers.map(p => (
          <Tile
            key={p.connectionId}
            stream={p.stream}
            displayName={p.displayName}
            photoData={p.photoData}
            micOn={p.micOn}
            cameraOn={p.cameraOn}
            avatarColor={colorForSeed(p.connectionId)}
          />
        ))}
      </div>
    </div>
  );
}

/* ─── Single video tile ─────────────────────────────────────────────────── */
function Tile({ stream, displayName, photoData, micOn, cameraOn, isLocal, avatarColor, main }) {
  const t = useT();
  const videoRef    = useRef(null);
  const tileRef     = useRef(null);
  const fsLockRef   = useRef(false); // prevent rapid repeated calls
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    // Always update srcObject so the browser releases the old stream when it changes
    el.srcObject = stream ?? null;
    if (stream) {
      // autoPlay can be silently blocked for sound (video still renders, audio doesn't) —
      // drive play() explicitly so we can detect and recover from that case.
      el.play().catch(() => {
        pendingPlays.add(el);
        ensureGestureListener();
      });
    }
    return () => { pendingPlays.delete(el); };
  }, [stream]);

  // Track fullscreen changes (e.g. user presses Escape).
  // Attach listener to the TILE element (not document) so each tile only
  // receives events for itself — avoids N-listener cascade with multiple tiles.
  useEffect(() => {
    const tile = tileRef.current;
    if (!tile) return;
    function onFsChange() {
      setIsFullscreen(document.fullscreenElement === tile);
    }
    tile.addEventListener('fullscreenchange', onFsChange);
    return () => tile.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  function toggleFullscreen() {
    if (fsLockRef.current) return; // block if transition already in progress
    fsLockRef.current = true;

    const promise = document.fullscreenElement
      ? document.exitFullscreen()
      : tileRef.current?.requestFullscreen();

    Promise.resolve(promise)
      .catch(() => {})
      .finally(() => { fsLockRef.current = false; });
  }

  const initials = (displayName || '?')
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('');

  const showVideo = !!stream && cameraOn;

  return (
    <div ref={tileRef} className={`vgrid__tile${main ? ' vgrid__tile--main' : ''}`}>
      {/* Video element — always mounted so ref is stable */}
      <video
        ref={videoRef}
        className={`vgrid__video${showVideo ? '' : ' vgrid__video--hidden'}${main ? ' vgrid__video--contain' : ''}`}
        autoPlay
        playsInline
        muted={isLocal}
      />

      {/* Avatar shown when camera is off */}
      {!showVideo && (
        <div className="vgrid__avatar">
          {photoData ? (
            <img className="vgrid__avatar-photo" src={photoData} alt="" />
          ) : (
            <div className="vgrid__avatar-circle" style={{ background: avatarColor }}>{initials}</div>
          )}
          <span className="vgrid__avatar-name">{displayName}</span>
        </div>
      )}

      {/* Fullscreen button — top-right corner, shown on hover */}
      {showVideo && (
        <button
          className="vgrid__fullscreen-btn"
          onClick={toggleFullscreen}
          title={isFullscreen ? t('room.exitFullscreen') : t('room.fullscreen')}
        >
          {isFullscreen ? <IconExitFullscreen /> : <IconFullscreen />}
        </button>
      )}

      {/* Name bar at the bottom */}
      <div className="vgrid__namebar">
        <span className="vgrid__name">{displayName}</span>
        {micOn ? (
          <svg className="vgrid__mic-svg vgrid__mic-svg--on" viewBox="0 0 24 24">
            <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V20H9v2h6v-2h-2v-2.08A7 7 0 0 0 19 11h-2z"/>
          </svg>
        ) : (
          <svg className="vgrid__mic-svg vgrid__mic-svg--off" viewBox="0 0 24 24">
            <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3 3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V20H9v2h6v-2h-2v-2.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/>
          </svg>
        )}
      </div>

      {/* "You" badge on local tile */}
      {isLocal && <span className="vgrid__you-badge">{t('room.you')}</span>}
    </div>
  );
}
